import dropdowncss from "../css/dropdown.module.css";
import commoncss from "../css/common.module.css";
import { useEffect, useState, useRef, useMemo } from "react";
import { ReactComponent as DownArrowIcon } from "../assets/icons/down_arrow.svg";
import { ReactComponent as UpArrowIcon } from "../assets/icons/up_arrow.svg";
import { ReqFieldLabel } from "../../myfin_v2/components/mis";

export default function ConsolDropdown({
  req = false,
  class_ = "",
  class2_ = "",
  label,
  options = [],
  onSelect,
  placeHolder,
  selected = {},
  selValue = null, // New prop
  disabled = false,
  openUpward = false,
  isEditable = false,
}) {
  const [open, setOpen] = useState(false);
  const [selectedOptionLabel, setSelectedOptionLabel] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    let labelToSet = null;

    if (selected === null && !selValue) {
      if (selectedOptionLabel !== null) {
        setSelectedOptionLabel(null);
        setInputValue("");
      }
      return;
    }

    if (options.length === 1) {
      labelToSet = options[0].label;
      if (selectedOptionLabel !== labelToSet) {
        setSelectedOptionLabel(labelToSet);
        if (document.activeElement !== inputRef.current) {
          setInputValue(labelToSet);
        }
        onSelect &&
          onSelect({ label: options[0].label, value: options[0].value });
      }
    } else if (selValue) {
      const matchedOption = options.find(
        (opt) =>
          String(opt.value).toLowerCase() === String(selValue).toLowerCase()
      );
      if (matchedOption && selectedOptionLabel !== matchedOption.label) {
        setSelectedOptionLabel(matchedOption.label);
        if (document.activeElement !== inputRef.current) {
          setInputValue(matchedOption.label);
        }
        onSelect &&
          onSelect({
            label: matchedOption.label,
            value: matchedOption.value,
          });
      }
    } else if (
      selected &&
      selected.label &&
      selected.label !== selectedOptionLabel
    ) {
      setSelectedOptionLabel(selected.label);
      if (document.activeElement !== inputRef.current) {
        setInputValue(selected.label);
      }
    }
  }, [options, selected, selValue, onSelect, selectedOptionLabel]);

  const handleSelect = (option) => {
    onSelect && onSelect({ label: option.label, value: option.value });
    setSelectedOptionLabel(option.label);
    setInputValue(option.label);
    setOpen(false);
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    setOpen(true);
  };

  const handleFocus = () => {
    setOpen(true);
  };

  const handleBlur = () => {
    if (inputValue === "") {
      onSelect && onSelect(null);
      setSelectedOptionLabel(null);
      return;
    }

    const matched = options.find(
      (opt) => String(opt.label).toLowerCase() === inputValue.toLowerCase()
    );
    if (matched) {
      onSelect && onSelect(matched);
      setSelectedOptionLabel(matched.label);
      setInputValue(matched.label);
    } else {
      onSelect && onSelect({ label: inputValue, value: inputValue });
      setSelectedOptionLabel(inputValue);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.target.blur();
    }
  };

  const handleDropdownClick = (e) => {
    if (disabled) return;
    if (isEditable) {
      if (inputRef.current) {
        inputRef.current.focus();
      }
      setOpen(true);
    } else {
      setOpen(!open);
    }
  };

  const filteredOptions = useMemo(() => {
    if (!isEditable || !inputValue) return options;
    if (inputValue === selectedOptionLabel) return options;
    return options.filter((opt) =>
      String(opt.label).toLowerCase().includes(inputValue.toLowerCase())
    );
  }, [options, inputValue, selectedOptionLabel, isEditable]);

  return (
    <div
      ref={dropdownRef}
      className={`${dropdowncss.dropdown_wrapper} ${class_}`}
      style={disabled ? { opacity: 0.6, pointerEvents: "none" } : {}}
    >
      {label && (
        <span className={dropdowncss.dropdown_label}>
          {req && <ReqFieldLabel />} &nbsp; {label}
        </span>
      )}
      <div
        className={`${dropdowncss.dropdown} ${class_}`}
        onClick={handleDropdownClick}
      >
        <div
          className={`${class2_} ${dropdowncss.dropdown_selected} ${
            (isEditable ? inputValue : selectedOptionLabel) ? dropdowncss.has_value : ""
          }`}
        >
          {isEditable ? (
            <>
              <style>{`
                .local_dropdown_input::placeholder {
                  color: #B3C0BD !important;
                  opacity: 1 !important;
                }
              `}</style>
              <input
                ref={inputRef}
                type="text"
                className={`${dropdowncss.dropdown_selected_text} local_dropdown_input`}
                style={{
                  border: "none",
                  outline: "none",
                  width: "100%",
                  background: "transparent",
                  color: "inherit",
                  fontSize: "inherit",
                  fontWeight: "inherit",
                  padding: 0,
                  margin: 0,
                  height: "18px",
                  lineHeight: "18px",
                }}
                value={inputValue}
                onChange={handleInputChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                placeholder={placeHolder}
                disabled={disabled}
              />
            </>
          ) : (
            <span 
              className={dropdowncss.dropdown_selected_text}
              title={selectedOptionLabel || ""}
            >
              {selectedOptionLabel || placeHolder}
            </span>
          )}
          <span className={dropdowncss.dropdown_arrow}>
            {open ? <UpArrowIcon /> : <DownArrowIcon />}
          </span>
        </div>

        <div
          className={`${dropdowncss.dropdown_menu} ${
            open ? dropdowncss.open : ""
          }`}
          style={openUpward ? { bottom: "100%", top: "auto", marginBottom: "5px", marginTop: "0" } : {}}
        >
          {filteredOptions.length === 0 ? (
            <div className={dropdowncss.dropdown_item} style={{ color: "#aaa", pointerEvents: "none" }}>
              No options found
            </div>
          ) : (
            filteredOptions.map((opt, i) => (
              <div
                key={i}
                className={dropdowncss.dropdown_item}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(opt);
                }}
              >
                {opt.label}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
